/* custom (Second Edition), owner cart redesign 2026-09-29 (design_handoff_cart_and_cart_popup).
   Cart page and cart popup behavior that the theme's cart.js and cart-drawer element do not cover:
   - keeps the Buunto Delivery Date Picker (source of truth for method, date and their cart attributes)
     inside the Delivery method card, and follows its method through data-mode on the cart root;
   - checkout gating: Buunto's own date check, then the required "How did you hear about us?" select;
   - the free-delivery upsell (Recently viewed / Similar pieces, update 2026-09-29) with in-place Add;
   - the popup's Keep shopping and close buttons, with focus returned to the cart icon;
   - the Delivery zones modal (Leaflet loaded on first open only). */
(() => {
  'use strict';
  if (window.seCart) return;
  window.seCart = true;

  const WAREHOUSE = [39.923325, -105.098177]; // US Census geocode of 2380 W Midway Blvd, the delivery-zone origin
  const ZONES = [
    { a: 0, b: 35, c: '#4F7148', t: '0–35 mi', d: 'Free on orders {min}+. Otherwise $149.' },
    { a: 35, b: 50, c: '#9BB38A', t: '35–50 mi', d: '$199. No free delivery threshold.' },
    { a: 50, b: 80, c: '#D9C27C', t: '50–80 mi', d: '$349. No free delivery threshold.' },
    { a: 80, b: 120, c: '#D98E6A', t: '80–120 mi', d: '$499. No free delivery threshold.' }
  ];

  const cartRoot = () => document.getElementById('Cart');

  /* ---------- Buunto: keep the widget in the card, follow its method ---------- */
  function placeBuunto() {
    const slot = document.getElementById('se-buunto-slot');
    const widget = document.getElementById('buunto-date-picker');
    if (slot && widget && widget.parentElement !== slot) slot.appendChild(widget);
  }
  function setMode(method) {
    const root = cartRoot();
    if (!root || !method) return;
    root.dataset.mode = String(method).toUpperCase().includes('PICKUP') ? 'pickup' : 'delivery';
  }
  function modeFromWidget() {
    const selected = document.querySelector('#buunto-date-picker .buunto-method-picker > label.selected');
    if (selected) setMode(selected.getAttribute('aria-label') === 'Store pickup' ? 'STORE_PICKUP' : 'LOCAL_DELIVERY');
  }
  document.addEventListener('BuuntoDatePicker:selectionChange', (e) => setMode(e.detail && e.detail.method));
  function watchBuunto() {
    const form = document.getElementById('cart-form');
    if (!form) return;
    placeBuunto();
    modeFromWidget();
    new MutationObserver(() => { placeBuunto(); modeFromWidget(); }).observe(form, { childList: true, subtree: true });
  }

  /* ---------- Checkout gating and the referral attribute ---------- */
  document.addEventListener('click', (e) => {
    const button = e.target.closest('#Cart [name="checkout"]');
    if (!button) return;
    const datePicker = window.Buunto && window.Buunto.datePicker;
    const widget = document.getElementById('buunto-date-picker');
    let dateMissing = false;
    if (datePicker && typeof datePicker.checkErrors === 'function') {
      dateMissing = datePicker.checkErrors() === true;
    } else if (widget) {
      // review 2026-09-29: the widget is on the page but its API is not (slow or renamed script); fall back to its
      // own fields so checkout never leaves without a delivery or pickup date.
      const dates = [...widget.querySelectorAll('[name="attributes[Delivery Date]"], [name="attributes[Pickup Date]"]')];
      dateMissing = !dates.some((field) => field.value);
    }
    if (dateMissing) {
      e.preventDefault();
      document.getElementById('se-delivery-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const referral = document.getElementById('referral_source');
    if (referral && !referral.value) {
      e.preventDefault();
      const error = document.getElementById('referral_source_error');
      if (error) error.hidden = false;
      referral.setAttribute('aria-invalid', 'true');
      referral.setAttribute('aria-describedby', 'referral_source_error');
      referral.scrollIntoView({ behavior: 'smooth', block: 'center' });
      referral.focus({ preventScroll: true });
    }
  });
  document.addEventListener('change', (e) => {
    if (e.target.id !== 'referral_source') return;
    const error = document.getElementById('referral_source_error');
    if (error) error.hidden = true;
    e.target.removeAttribute('aria-invalid');
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { 'How did you hear about us?': e.target.value } })
    }).catch(() => {});
  });

  /* ---------- Cart page re-render (items, summary, header bubble) ---------- */
  function swap(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector(selector);
    const current = document.querySelector(selector);
    if (next && current) current.innerHTML = next.innerHTML;
  }
  async function refreshCartPage() {
    const response = await fetch(`${window.location.pathname}?sections=main-cart,cart-bubble`);
    const sections = await response.json();
    const count = (sections['main-cart'] || '').match(/class="se-line se-line--page"/g);
    if (!count) { window.location.reload(); return; }
    swap(sections['main-cart'], '#se-cart-items');
    swap(sections['main-cart'], '#se-cart-summary');
    swap(sections['main-cart'], '#se-ups-card');
    swap(sections['main-cart'], '.thb-cart-form--count');
    ['.thb-secondary-area--item-count', '.thb-secondary-area--item-amount'].forEach((selector) => {
      if (sections['cart-bubble']) swap(sections['cart-bubble'], selector);
    });
    document.dispatchEvent(new CustomEvent('se:cart-rendered'));
  }
  document.addEventListener('line-item:change:end', (e) => {
    if (!cartRoot()) return;
    if (e.detail && e.detail.cart && e.detail.cart.item_count === 0) { window.location.reload(); }
  });

  /* ---------- Free-delivery upsell (cart upsell update 2026-09-29, design_handoff_cart_upsell) ----------
     Recently viewed (the theme's own localStorage['recently-viewed'] handles, written by product.js on every
     product page) and Similar pieces (our Similar Products ranking for the most expensive cart piece). One
     block, mounted in the desktop card under the line items (.wide carousel) or, on phones, inside the Order
     summary (vertical list). Liquid renders a slot only while a cart is under the free-delivery minimum; the
     mode rules (pickup hides it) are CSS on data-mode. Products already in the cart, sold pieces and pre-order
     (Arriving Soon) pieces are left out. Sort: pieces that close the gap first, then price ascending. */
  const UPS_MAX = 6;
  const productCache = new Map(); // handle or id -> product JSON (or null when it could not be read)
  const ups = { node: null, tab: null, seen: {}, key: '' };
  const wideQuery = window.matchMedia('(min-width: 768px)');

  function track(name, data) {
    // A/B measurement: Shopify customer events. A custom pixel (Settings > Customer events) can subscribe to
    // se_cart_upsell_* and forward them to analytics. Never blocks the page.
    try { window.Shopify && Shopify.analytics && Shopify.analytics.publish && Shopify.analytics.publish(`se_cart_upsell_${name}`, data || {}); } catch (err) { /* ignore */ }
  }
  const money = (cents) => '$' + Math.round(cents / 100).toLocaleString('en-US');
  const esc = (text) => String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  function imageUrl(src, width) {
    if (!src) return '';
    const url = String(src).startsWith('//') ? `https:${src}` : String(src);
    return url + (url.includes('?') ? '&' : '?') + `width=${width}`;
  }
  function usable(p, inCart) {
    if (!p || !p.available || inCart.has(String(p.id))) return false;
    const tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',');
    return !tags.some((t) => String(t).trim().toLowerCase() === 'arriving-soon');
  }
  function firstVariant(p) {
    return (p.variants || []).find((v) => v.available) || (p.variants || [])[0];
  }
  async function productByHandle(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    let p = null;
    try {
      const r = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
      if (r.ok) p = await r.json();
    } catch (err) { p = null; }
    productCache.set(handle, p);
    return p;
  }
  async function recentlyViewed(inCart) {
    let handles = [];
    try { handles = JSON.parse(window.localStorage.getItem('recently-viewed') || '[]'); } catch (err) { handles = []; }
    if (!Array.isArray(handles)) return [];
    const products = await Promise.all([...new Set(handles)].slice(0, 12).map(productByHandle));
    return products.filter((p) => usable(p, inCart));
  }
  // Owner 2026-09-29: Similar pieces uses our Similar Products ranking, not Shopify's related picks. The product
  // page's section (sections/product-recommendations.liquid) is rendered for the most expensive cart piece; its
  // scored cards from every catalog page are merged by assets/custom-similar-products.js (window.seSimilarRank:
  // score order, twin titles once, at most two per brand). Cart pieces are left out before ranking, and the six
  // best usable pieces are kept in similarity order before the display sort.
  // Rendered by file name (a template's own section key does not work here), so the schema default applies: 6, like
  // every product template, which keeps the best 12 per catalog page.
  const SIMILAR_SECTIONS = ['product-recommendations'];
  async function similarSlides(url) {
    for (const id of SIMILAR_SECTIONS) {
      try {
        const base = `${url}${url.includes('?') ? '&' : '?'}section_id=${id}`;
        const r = await fetch(base, { credentials: 'same-origin' });
        if (!r.ok) continue;
        const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
        const grid = doc.querySelector('[data-se-similar]');
        if (!grid) continue;
        let slides = window.seSimilarSlidesFrom(doc.body);
        const pages = Number(grid.dataset.sePages) || 1;
        if (pages > 1) {
          const others = await Promise.all(Array.from({ length: pages - 1 }, (_, i) => window.seSimilarFetchPage(base, i + 2)));
          others.forEach((list) => { slides = slides.concat(list); });
        }
        return slides;
      } catch (err) { /* try the next section id */ }
    }
    return [];
  }
  async function similar(topUrl, inCart, cartHandles) {
    if (!topUrl || typeof window.seSimilarRank !== 'function') return [];
    const key = `sim:${topUrl}`;
    if (!productCache.has(key)) productCache.set(key, await similarSlides(topUrl));
    const slides = productCache.get(key).filter((slide) => !cartHandles.has(slide.dataset.seHandle));
    const ranked = window.seSimilarRank(slides, UPS_MAX * 2);
    const products = await Promise.all(ranked.map((slide) => productByHandle(slide.dataset.seHandle)));
    return products.filter((p) => usable(p, inCart)).slice(0, UPS_MAX);
  }
  function arrange(list, gap) {
    return list
      .map((p) => ({ p, v: firstVariant(p) }))
      .filter((x) => x.v)
      .sort((a, b) => ((b.v.price >= gap) - (a.v.price >= gap)) || (a.v.price - b.v.price))
      .slice(0, UPS_MAX);
  }
  function tileHtml({ p, v }, gap) {
    const compare = v.compare_at_price && v.compare_at_price > v.price ? `<s>${money(v.compare_at_price)}</s>` : '';
    const image = p.featured_image || (p.images || [])[0];
    const well = image
      ? `<img src="${esc(imageUrl(image, 300))}" srcset="${esc(imageUrl(image, 150))} 150w, ${esc(imageUrl(image, 300))} 300w, ${esc(imageUrl(image, 450))} 450w" sizes="(min-width: 768px) 220px, 88px" alt="" loading="lazy">`
      : '';
    const unlock = v.price >= gap
      ? '<span class="se-ups__tag">Unlocks free delivery</span>'
      : `<span class="se-ups__short">${money(gap - v.price)} short of free delivery</span>`;
    return `<div class="se-ups__tile" data-se-product="${p.id}">
        <a class="se-ups__well" href="${esc(p.url || `/products/${p.handle}`)}" tabindex="-1" aria-hidden="true">${well}</a>
        <div class="se-ups__info">
          <a class="se-ups__name" href="${esc(p.url || `/products/${p.handle}`)}">${esc(p.title)}</a>
          <div class="se-ups__price">${compare}${money(v.price)}</div>
          ${unlock}
        </div>
        <button type="button" class="se-ups__add" data-se-add="${v.id}" aria-label="Add ${esc(p.title)} to cart">Add</button>
      </div>`;
  }
  const CHEVRON = '<svg class="se-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m15 6-6 6 6 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  function buildUps(lists, gap) {
    const tabs = [];
    if (lists.rv.length) tabs.push({ id: 'rv', label: 'Recently viewed', items: lists.rv });
    if (lists.sim.length) tabs.push({ id: 'sim', label: 'Similar pieces', items: lists.sim });
    if (!tabs.length) return null;
    if (!tabs.some((t) => t.id === ups.tab)) ups.tab = tabs[0].id;
    const solo = tabs.length === 1;
    const node = document.createElement('div');
    node.className = 'se-ups';
    node.innerHTML = `<div class="se-ups__tabs"${solo ? '' : ' role="tablist" aria-label="Pieces that get you closer to free delivery"'}>
        ${tabs.map((t) => solo
          ? `<span class="se-ups__tab is-on se-ups__tab--solo">${t.label}</span>`
          : `<button type="button" class="se-ups__tab${t.id === ups.tab ? ' is-on' : ''}" role="tab" id="se-ups-tab-${t.id}" aria-controls="se-ups-list-${t.id}" aria-selected="${t.id === ups.tab}" tabindex="${t.id === ups.tab ? 0 : -1}" data-se-tab="${t.id}">${t.label}</button>`).join('')}
        <span class="se-ups__arrows">
          <button type="button" class="se-ups__arrow" data-se-dir="-1" aria-label="Previous">${CHEVRON}</button>
          <button type="button" class="se-ups__arrow se-ups__arrow--next" data-se-dir="1" aria-label="Next">${CHEVRON}</button>
        </span>
      </div>
      ${tabs.map((t) => `<div class="se-ups__list" data-se-list="${t.id}" id="se-ups-list-${t.id}"${solo ? ' aria-label="Similar pieces"' : ` role="tabpanel" aria-labelledby="se-ups-tab-${t.id}"`}${t.id === ups.tab ? '' : ' hidden'}>${t.items.map((x) => tileHtml(x, gap)).join('')}</div>`).join('')}
      <p class="se-ups__more">Scroll for more</p>`;
    return node;
  }
  // Owner 2026-09-29: phones show two full rows plus a peek of the third (the handoff had three). Rows differ in
  // height (names wrap), so the box is measured from the real second row instead of a fixed max-height.
  const STACK_ROWS = 2;
  const STACK_PEEK = 44;
  function fitStack(list) {
    const rows = list.children;
    if (rows.length <= STACK_ROWS) { list.style.maxHeight = 'none'; return; }
    const last = rows[STACK_ROWS - 1];
    const height = last.offsetTop + last.offsetHeight - rows[0].offsetTop + STACK_PEEK;
    if (height > STACK_PEEK) list.style.maxHeight = `${height}px`;
  }
  const activeList = () => ups.node && ups.node.querySelector('.se-ups__list:not([hidden])');
  function updateUps() {
    const list = activeList();
    if (!list) return;
    const wide = ups.node.classList.contains('is-wide');
    if (wide) list.style.maxHeight = '';
    const [prev, next] = ups.node.querySelectorAll('.se-ups__arrow');
    const atStart = list.scrollLeft < 4;
    const atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 4;
    [[prev, !wide || atStart], [next, !wide || atEnd]].forEach(([arrow, off]) => {
      arrow.disabled = off;
      arrow.setAttribute('aria-hidden', String(off));
      arrow.tabIndex = off ? -1 : 0;
    });
    if (!wide) fitStack(list);
    const end = list.scrollTop + list.clientHeight >= list.scrollHeight - 4;
    list.classList.toggle('is-end', end);
    const more = ups.node.querySelector('.se-ups__more');
    if (more) more.style.visibility = end ? 'hidden' : 'visible';
  }
  function seenTab() {
    const list = activeList();
    if (!list || ups.seen[ups.tab]) return;
    const root = cartRoot();
    if (!ups.node.offsetParent || (root && root.dataset.mode === 'pickup')) return; // counted only when actually visible
    ups.seen[ups.tab] = true;
    track('impression', { tab: ups.tab, layout: wideQuery.matches ? 'wide' : 'stack', count: list.children.length });
  }
  // arrow and fade state also follow layout changes (viewport resize, images and fonts arriving)
  const upsResize = 'ResizeObserver' in window ? new ResizeObserver(() => updateUps()) : null;
  function mountUps() {
    const slot = document.querySelector(`[data-se-ups-slot="${wideQuery.matches ? 'wide' : 'stack'}"]`);
    if (!ups.node || !slot) return;
    if (upsResize && !ups.node._observed) { ups.node._observed = true; ups.node.querySelectorAll('.se-ups__list').forEach((list) => upsResize.observe(list)); }
    ups.node.classList.toggle('is-wide', wideQuery.matches);
    if (ups.node.parentElement !== slot) slot.appendChild(ups.node);
    updateUps();
    seenTab();
  }
  let upsTicket = 0;
  async function loadUps() {
    const slot = document.querySelector('[data-se-ups-slot]');
    const gapEl = document.querySelector('.se-cart [data-se-gap]');
    if (!slot || !gapEl) { ups.node = null; return; }
    const gap = Number(gapEl.dataset.seGap) || 0;
    const inCart = new Set(String(slot.dataset.seCart || '').split('|').filter(Boolean));
    const cartHandles = new Set(String(slot.dataset.seCartHandles || '').split('|').filter(Boolean));
    const key = `${gap}|${slot.dataset.seCart}|${slot.dataset.seTopUrl}`;
    if (key === ups.key && ups.node) { mountUps(); return; }
    const ticket = ++upsTicket;
    const [rv, sim] = await Promise.all([recentlyViewed(inCart), similar(slot.dataset.seTopUrl, inCart, cartHandles)]);
    if (ticket !== upsTicket) return;
    ups.key = key;
    ups.node = buildUps({ rv: arrange(rv, gap), sim: arrange(sim, gap) }, gap);
    mountUps();
  }
  document.addEventListener('se:cart-rendered', () => {
    if (!cartRoot()) return;
    if (!document.querySelector('[data-se-ups-slot]') && ups.node) track('threshold_reached', {});
    loadUps();
  });
  wideQuery.addEventListener('change', mountUps);
  document.addEventListener('BuuntoDatePicker:selectionChange', () => setTimeout(seenTab, 0));
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('.se-ups [data-se-tab]');
    if (tab) {
      ups.tab = tab.dataset.seTab;
      ups.node.querySelectorAll('[data-se-tab]').forEach((b) => {
        const on = b === tab;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-selected', String(on));
        b.tabIndex = on ? 0 : -1;
      });
      ups.node.querySelectorAll('.se-ups__list').forEach((list) => {
        list.hidden = list.dataset.seList !== ups.tab;
        list.scrollLeft = 0;
        list.scrollTop = 0;
      });
      track('tab_switch', { tab: ups.tab });
      updateUps();
      seenTab();
      return;
    }
    const arrow = e.target.closest('.se-ups__arrow');
    if (arrow) {
      const list = activeList();
      if (!list) return;
      list.scrollBy({ left: Number(arrow.dataset.seDir) * (list.clientWidth + 12) });
      track('arrow', { tab: ups.tab, direction: Number(arrow.dataset.seDir) > 0 ? 'next' : 'previous' });
    }
  });
  // Arrow keys move between the two tabs (WAI-ARIA tabs pattern)
  document.addEventListener('keydown', (e) => {
    const tab = e.target.closest && e.target.closest('.se-ups [data-se-tab]');
    if (!tab || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
    const all = [...ups.node.querySelectorAll('[data-se-tab]')];
    const next = all[(all.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : all.length - 1)) % all.length];
    e.preventDefault();
    next.focus();
    next.click();
  });
  const scrollDepth = {};
  document.addEventListener('scroll', (e) => {
    const list = e.target;
    if (!list.classList || !list.classList.contains('se-ups__list')) return;
    updateUps();
    const wide = ups.node.classList.contains('is-wide');
    const depth = wide ? (list.scrollLeft + list.clientWidth) / list.scrollWidth : (list.scrollTop + list.clientHeight) / list.scrollHeight;
    const bucket = Math.min(100, Math.round(depth * 4) * 25);
    const k = `${ups.tab}:${wide}`;
    if (bucket > (scrollDepth[k] || 0)) {
      scrollDepth[k] = bucket;
      track('scroll_depth', { tab: ups.tab, layout: wide ? 'wide' : 'stack', percent: bucket });
    }
  }, true);

  /* ---------- Add to cart from the upsell (and anything else carrying data-se-add) ---------- */
  document.addEventListener('click', async (e) => {
    const button = e.target.closest('[data-se-add]');
    if (!button || button.disabled) return;
    e.preventDefault();
    const tile = button.closest('.se-ups__tile');
    if (tile) {
      const list = tile.parentElement;
      track('add', { tab: list.dataset.seList, position: [...list.children].indexOf(tile) + 1, product_id: Number(tile.dataset.seProduct), layout: ups.node.classList.contains('is-wide') ? 'wide' : 'stack' });
    }
    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    let failure = null;
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.seAdd), quantity: 1 }] })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        failure = body.description || body.message || 'This piece could not be added.';
      }
    } catch (err) {
      failure = 'This piece could not be added. Please try again.';
    }
    if (failure) {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      // the theme's standard add-to-cart error: Shopify's message inline under the button (as product.js does)
      const host = tile || button.parentElement;
      let error = host.querySelector('.se-ups__error');
      if (!error) {
        error = document.createElement('p');
        error.className = 'se-ups__error';
        error.setAttribute('role', 'alert');
        host.appendChild(error);
      }
      error.textContent = failure;
      return;
    }
    try {
      if (cartRoot()) await refreshCartPage();
      else document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (err) {
      window.location.reload(); // the piece is in the cart; show it rather than a stale page
    }
  });

  // review 2026-09-29: Remove is a cart-remove element with role=button; Enter and Space activate it like a click
  document.addEventListener('keydown', (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && e.target.matches && e.target.matches('cart-remove.se-line__remove')) {
      e.preventDefault();
      e.target.click();
    }
  });

  /* ---------- Popup: Keep shopping / close, focus back to the cart icon ---------- */
  function closeDrawer() {
    const drawer = document.getElementById('Cart-Drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
    document.getElementById('cart-drawer-toggle')?.focus();
  }
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-se-drawer-close]')) { e.preventDefault(); closeDrawer(); }
  });
  document.addEventListener('keyup', (e) => {
    if (e.key !== 'Escape' || document.querySelector('.se-zones.is-open')) return;
    const drawer = document.getElementById('Cart-Drawer');
    if (drawer && drawer.hasAttribute('inert') && document.activeElement && drawer.contains(document.activeElement)) {
      document.getElementById('cart-drawer-toggle')?.focus();
    }
  });

  /* ---------- Delivery zones modal ---------- */
  let leafletPromise = null;
  function loadLeaflet() {
    if (window.L) return Promise.resolve();
    if (leafletPromise) return leafletPromise;
    leafletPromise = new Promise((resolve, reject) => {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      css.integrity = 'sha384-sHL9NAb7lN7rfvG5lfHpm643Xkcjzp4jFvuavGOndn6pjVqS6ny56CAt3nsEVT4H';
      css.crossOrigin = 'anonymous';
      document.head.appendChild(css);
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.integrity = 'sha384-cxOPjt7s7Iz04uaHJceBmS+qpjv2JkIHNVcuOrM+YHwZOmJGBXI00mdUXEq65HTH';
      script.crossOrigin = 'anonymous';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return leafletPromise;
  }
  function ring(miles) {
    const R = 3958.8;
    const points = [];
    for (let i = 0; i <= 128; i++) {
      const t = (i / 128) * 2 * Math.PI;
      const dLat = (miles / R) * Math.cos(t);
      const dLng = (miles / (R * Math.cos((WAREHOUSE[0] * Math.PI) / 180))) * Math.sin(t);
      points.push([WAREHOUSE[0] + (dLat * 180) / Math.PI, WAREHOUSE[1] + (dLng * 180) / Math.PI]);
    }
    return points;
  }
  let zones = null;
  let zonesTrigger = null;
  function buildZones() {
    zones = document.createElement('div');
    zones.className = 'se-zones';
    zones.setAttribute('role', 'dialog');
    zones.setAttribute('aria-modal', 'true');
    zones.setAttribute('aria-labelledby', 'se-zones-title');
    // review 2026-09-29: the free-delivery minimum comes from the page (scheduled events change it), not a fixed $1,499
    const cents = Number(document.querySelector('[data-se-threshold]')?.dataset.seThreshold) || 149900;
    const min = '$' + Math.round(cents / 100).toLocaleString('en-US');
    const legend = ZONES.map((z) => `<div class="se-zones__row"><i style="background:${z.c}"></i><div><b>${z.t}</b>${z.d.replace('{min}', min)}</div></div>`).join('') +
      '<div class="se-zones__row"><i class="se-zones__none"></i><div><b>Beyond 120 mi</b>No delivery. Broomfield pickup is always free.</div></div>';
    zones.innerHTML = `<div class="se-zones__panel">
        <div class="se-zones__head">
          <div><h2 class="se-zones__title" id="se-zones-title">Delivery zones</h2></div>
          <button type="button" class="se-zones__close" aria-label="Close">&times;</button>
        </div>
        <div class="se-zones__body"><div class="se-zones__map" aria-label="Map of delivery zones"></div><div class="se-zones__legend">${legend}</div></div>
      </div>`;
    document.body.appendChild(zones);
    // Clicks and Esc inside the modal must not also reach the popup's outside-click and Esc handlers.
    zones.addEventListener('click', (e) => { e.stopPropagation(); if (e.target === zones || e.target.closest('.se-zones__close')) closeZones(); });
    zones.addEventListener('keyup', (e) => e.stopPropagation());
    zones.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); closeZones(); return; }
      if (e.key !== 'Tab') return;
      const focusable = [...zones.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }
  async function openZones(trigger) {
    zonesTrigger = trigger;
    if (!zones) buildZones();
    zones.classList.add('is-open');
    document.documentElement.classList.add('se-zones-lock');
    zones.querySelector('.se-zones__close').focus();
    try { await loadLeaflet(); } catch (err) { zones.querySelector('.se-zones__map').textContent = 'The map could not load. The zones are listed here.'; return; }
    if (!zones._map) {
      const L = window.L;
      const map = L.map(zones.querySelector('.se-zones__map'), { scrollWheelZoom: false, zoomSnap: 0.25 });
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap contributors', maxZoom: 18 }).addTo(map);
      ZONES.slice().reverse().forEach((z) => {
        L.polygon(z.a ? [ring(z.b), ring(z.a)] : [ring(z.b)], { color: z.c, weight: 1.5, fillColor: z.c, fillOpacity: 0.32 }).addTo(map);
      });
      L.circleMarker(WAREHOUSE, { radius: 7, color: '#FFFFFF', weight: 2, fillColor: '#2F2F2F', fillOpacity: 1 }).addTo(map).bindTooltip('Second Edition warehouse', { direction: 'top' });
      zones._map = map;
    }
    zones._map.invalidateSize();
    zones._map.fitBounds(window.L.latLngBounds(ring(120)), { padding: [8, 8] });
  }
  function closeZones() {
    if (!zones) return;
    zones.classList.remove('is-open');
    document.documentElement.classList.remove('se-zones-lock');
    zonesTrigger?.focus();
  }
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.se-zone-link');
    if (!link) return;
    e.preventDefault();
    e.stopPropagation();
    openZones(link);
  }, true);

  function init() {
    watchBuunto();
    loadUps();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
